import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAgents } from "@/api/queries/agents";
import { useSetBindingAgent } from "@/api/mutations/channelBindings";
import type { ChannelBinding } from "@/types";

const FALLBACK_VALUE = "__fallback__";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  binding: ChannelBinding;
}

export function EditBindingAgentDialog({ open, onOpenChange, userId, binding }: Props) {
  const agentsQuery = useAgents();
  const allAgents = agentsQuery.data ?? [];
  const [selection, setSelection] = useState<string>(binding.agentId ?? FALLBACK_VALUE);
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = useSetBindingAgent(userId);

  const eligibleAgents = useMemo(
    () =>
      allAgents.filter((a) => a.ownerId === userId || a.type === "service-agent"),
    [allAgents, userId],
  );

  function handleSubmit() {
    setServerError(null);
    const agentId = selection === FALLBACK_VALUE ? null : selection;
    mutation.mutate(
      { id: binding.id, agentId },
      {
        onSuccess: () => {
          toast.success("Agent-Target aktualisiert");
          onOpenChange(false);
        },
        onError: (err) => {
          const msg = (err as Error).message ?? "Unbekannter Fehler";
          setServerError(msg);
          toast.error(`Update fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-binding-agent-dialog">
        <DialogHeader>
          <DialogTitle>Agent-Target ändern</DialogTitle>
          <DialogDescription>
            Welcher Agent kriegt Nachrichten von <span className="font-mono">{binding.externalId}</span>?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Agent</Label>
            <Select value={selection} onValueChange={setSelection}>
              <SelectTrigger data-testid="edit-binding-agent-field">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={FALLBACK_VALUE}>— Fallback (Master leitet ab)</SelectItem>
                {eligibleAgents.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.id} ({a.type === "service-agent" ? "service" : "owned"})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {serverError && (
          <div className="text-sm text-red" data-testid="edit-binding-agent-error">
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={mutation.isPending} data-testid="edit-binding-agent-submit">
            {mutation.isPending ? "…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
